import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromCreateOrder from '@complete-order/store/actions/create.order.action';
import {
  CardPaymentRedirectRequest,
  CardPaymentRedirectResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  DeliveryDatesRequest,
  DirectDebitValidationRequest,
  DirectDebitValidationResponse,
  ErrorType,
  startLightstepSpan,
} from '@dgx-pr-spa/shared/util';
import { Store } from '@ngrx/store';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { OfferBackendService } from '@view-offers/services/offer-backend.service';
import {
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  takeWhile,
  throwError,
  TimeoutError,
  timer,
} from 'rxjs';
import { catchError, map, pluck, retry, tap } from 'rxjs/operators';

export enum Caller {
  DELIVERY_DATES = 'delivery dates',
  CREATE_ORDER = 'create order',
  CARD_REDIRECT = 'card redirect',
}
@Injectable({
  providedIn: 'root',
})
export class CompleteOrderBackendService {
  pollingForStatus;
  isProcessingPayment = false;
  isAtStageToPoll = false;
  orderCached = false;
  attemptingToCacheOrder = new ReplaySubject(1);
  pollingStatus: string;

  constructor(
    public http: HttpClient,
    private router: Router,
    private store: Store,
    private productDetailsService: ProductDetailsService,
    private featureFlagService: FeatureFlagService,
    private readonly offerBackendService: OfferBackendService
  ) {}

  fetch<T>(url: string, caller?: string): Observable<T> {
    // this.handleStatus({} as Response, caller);
    // return throwError('error');

    return this.http.get(url).pipe(
      catchError((error: Response) => {
        this.handleStatus(error, caller, url);
        return throwError(error);
      })
    ) as Observable<T>;
  }

  post<T>(url: string, data, redirectOnError?, caller?): Observable<T> {
    return this.http
      .post(url, JSON.stringify(data), {
        headers: new HttpHeaders({
          'content-type': 'application/json',
        }),
        observe: 'response',
      })
      .pipe(
        map((response) =>
          caller === Caller.CREATE_ORDER && response.status === 201
            ? ({ error_status: 201 } as CreateOrderResponse)
            : response.body
        ),
        catchError((error: Response) => {
          if (redirectOnError) {
            this.handleStatus(error, caller);
          }
          return throwError(error);
        })
      ) as Observable<T>;
  }

  fetchDeliveryDates(url: string, payload: DeliveryDatesRequest) {
    const params: Record<string, string> = {
      catNum: `${payload.cat_num}`,
      postCode: `${payload.post_code}`,
      supplierId: `${payload.supplier_id}`,
    };

    params.serviceCatNumbers = payload.service_cat_numbers?.length
      ? payload.service_cat_numbers.join(',')
      : 'null';

    return this.http.get(
      `${this.featureFlagService.loadedConfig.new_API}/api/v1/offers/${payload.offer_reference}/deliverydates`,
      {
        params,
      }
    );
  }

  postDirectDebit(
    url: string,
    data: DirectDebitValidationRequest
  ): Observable<DirectDebitValidationResponse | unknown> {
    // return of(mockDirectDebitValidationResponse);
    return this.post<DirectDebitValidationResponse>(url, data).pipe(
      pluck('result')
    );
  }

  postCreateOrder(
    url: string,
    data: CreateOrderRequest
  ): Observable<CreateOrderResponse | unknown> {
    return this.http
      .post(
        `${this.featureFlagService.loadedConfig.new_API}/api/v1/offers/${data.offer_reference}/order`,
        {
          ...data,
          offer_response: this.offerBackendService.itemOffer,
        }
      )
      .pipe(this.withTimeoutRetry());
  }

  postCardPaymentRedirect(
    url: string,
    data: CardPaymentRedirectRequest
  ): Observable<CardPaymentRedirectResponse | unknown> {
    // return of(mockCardPaymentRedirectResponse);
    return this.post<any>(
      url,
      data,
      true /* handle error */,
      Caller.CARD_REDIRECT
    ).pipe(pluck('result'));
  }

  handleStatus(error: Response, caller: string, url?: string) {
    const status = error?.status || '';
    const statusText = error?.statusText || '';

    const msg = `${status} ${statusText} ${caller}`;
    if (caller !== Caller.DELIVERY_DATES) {
      this.router.navigate(['/error'], {
        queryParams: { msg },
        replaceUrl: true,
      });
    } else {
      this.productDetailsService.handleDeliveryApiError({
        delivery_dates: null,
        message: ErrorType.DELIVERY_API_ERROR,
        status,
        statusText,
        url,
      });
    }
  }

  pollApi(offerReference: string, interval = 3000): Observable<any> {
    return timer(0, interval).pipe(
      switchMap(() =>
        this.http.post<any>(
          `${this.featureFlagService.loadedConfig.new_API}/api/v1/order-status`,
          { offerReference }
        )
      ),
      tap((response) => {
        console.log('Polling response:', response);
      }),
      takeWhile(
        (response) =>
          !['COMPLETE', 'ERROR'].includes(response?.status) &&
          this.isAtStageToPoll,
        true // Include the last value when the condition becomes false
      ),
      shareReplay(1) // Share the observable to avoid multiple subscriptions to the HTTP call
    );
  }

  submitOrderForOffer(data: CreateOrderRequest) {
    this.attemptingToCacheOrder.next(true);
    this.orderCached = false; // In case someone caches first, then goes back in
    return this.post(
      `${this.featureFlagService.loadedConfig.new_API}/api/v1/submit-order`,
      {
        paymentReference: data.card_payment_details.order_number,
        offerReference: data.offer_reference,
        planNumber: `${data.card_payment_details.scheme_code}${data.card_payment_details.reference_number}`,
        orderDetails: {
          ...data,
          offer_response: this.offerBackendService.itemOffer,
        },
      }
    ).pipe(
      catchError((error) => {
        startLightstepSpan('pr.online.submitOrderFailed', {
          orderReference: data.card_payment_details.order_number,
          prRef: data.offer_reference,
        }).end();
        return throwError(() => error);
      })
    );
  }

  cacheOrderForOffer(data: CreateOrderRequest) {
    if (!this.isAtStageToPoll) return of({});
    this.attemptingToCacheOrder.next(true);
    this.orderCached = false; // In case someone caches first, then goes back in
    return this.post(
      `${this.featureFlagService.loadedConfig.new_API}/api/v1/cache-order`,
      {
        paymentReference: data.card_payment_details.order_number,
        offerReference: data.offer_reference,
        planNumber: `${data.card_payment_details.scheme_code}${data.card_payment_details.reference_number}`,
        orderDetails: {
          ...data,
          offer_response: this.offerBackendService.itemOffer,
        },
      }
    ).pipe(
      retry(2),
      tap(() => {
        this.orderCached = true;
        this.attemptingToCacheOrder.next(false);
        this.setupPollingAPI(data.offer_reference);
      }),
      catchError((error) => {
        startLightstepSpan('pr.online.paymentFallback', {
          orderReference: data.card_payment_details.order_number,
          prRef: data.offer_reference,
        }).end();
        this.attemptingToCacheOrder.next(false);
        return throwError(() => error);
      })
    );
  }

  setupPollingAPI(offerReference: string) {
    this.pollingForStatus?.unsubscribe?.();

    this.pollingForStatus = this.pollApi(offerReference);

    this.pollingForStatus.subscribe({
      next: (data) => {
        this.pollingStatus = data.status;
        if (data.status === 'AUTHORISED' || data.status === 'PLACING_ORDER') {
          this.isProcessingPayment = true;
          sessionStorage.setItem(`payment_taken_${offerReference}`, 'true');
          this.router.navigate(['processing-order'], {
            queryParamsHandling: 'preserve',
            replaceUrl: true,
          });
          return;
        }

        this.isProcessingPayment = false;

        if (data.status === 'ERROR') {
          this.pollingForStatus?.unsubscribe?.();
          this.store.dispatch(
            fromCreateOrder.loadCreateOrderFail({
              payload: 'error',
            })
          );
          this.router.navigate(['offer-error'], {
            queryParamsHandling: 'preserve',
            replaceUrl: true,
          });
          return;
        }

        if (data.status === 'COMPLETE') {
          this.pollingForStatus?.unsubscribe?.();
          this.store.dispatch(
            fromCreateOrder.loadCreateOrderSuccess({
              payload: data.bvResponse,
            })
          );

          this.router.navigate(['order-confirmation'], {
            queryParamsHandling: 'preserve',
            replaceUrl: true,
          });
          return;
        }
      },
    });
    return this.pollingForStatus;
  }

  private isTimeoutError(error: unknown): boolean {
    if (error instanceof TimeoutError) return true;

    if (error instanceof HttpErrorResponse) {
      const status = error.status;
      const text = (error.statusText || '').toLowerCase();
      const msg = (error.message || '').toLowerCase();
      if (status === 504) return true;
      if (status === 0 && (text.includes('timeout') || msg.includes('timeout')))
        return true;
      return false;
    }

    if (error instanceof Error) {
      const m = error.message.toLowerCase();
      return m.includes('timeout') || m.includes('timed out');
    }
    return false;
  }

  private withTimeoutRetry<T>() {
    return (source: Observable<T>) =>
      source.pipe(
        retry({
          count: 2,
          delay: (err) => {
            if (this.isTimeoutError(err)) {
              return timer(5000);
            }
            throw err;
          },
        })
      );
  }
}
